// src/features/legal/DisclaimerGate.jsx
// Blocking acceptance screen shown once per user before the app is accessible.
// User must explicitly tap "I Understand" — no implicit consent.
//
// Shows again if DISCLAIMER_VERSION is bumped (material ToS change).
// State stored in localStorage (not Zustand — must survive before store hydrates).

import { useState } from 'react';
import {
  EMERGENCY_DISCLAIMER,
  DISCLAIMER_ACCEPTED_KEY,
  DISCLAIMER_VERSION,
} from '@data/legal';
import { T } from '@components/theme';

// ── Accept / check helpers (used by App.jsx) ──────────────────────
export function hasAcceptedDisclaimer() {
  try {
    const raw = localStorage.getItem(DISCLAIMER_ACCEPTED_KEY);
    if (!raw) return false;
    const stored = JSON.parse(raw);
    return stored.version >= DISCLAIMER_VERSION;
  } catch {
    return false;
  }
}

export function recordDisclaimerAccepted() {
  try {
    localStorage.setItem(DISCLAIMER_ACCEPTED_KEY, JSON.stringify({
      version:    DISCLAIMER_VERSION,
      acceptedAt: new Date().toISOString(),
    }));
  } catch {}
}

// ── DisclaimerGate component ──────────────────────────────────────
export function DisclaimerGate({ onAccept }) {
  const [checked, setChecked] = useState(false);
  const [showFull, setShowFull] = useState(false);

  const accept = () => {
    if (!checked) return;
    recordDisclaimerAccepted();
    onAccept();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: '#0a0a0a',
      display: 'flex', flexDirection: 'column',
      maxWidth: 430, margin: '0 auto',
      fontFamily: T.font,
    }}>
      {/* Red header bar */}
      <div style={{
        background: '#7f1d1d',
        padding: '20px 20px 16px',
        flexShrink: 0,
      }}>
        <div style={{ fontSize: 28, marginBottom: 8 }}>🚨</div>
        <div style={{
          fontWeight: 900, fontSize: 20, color: '#fff',
          lineHeight: 1.2, marginBottom: 4,
        }}>
          {EMERGENCY_DISCLAIMER.headline}
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
          Read before using Eye on Storm
        </div>
      </div>

      {/* Scrollable body */}
      <div style={{
        flex: 1, overflowY: 'auto',
        padding: '20px',
        WebkitOverflowScrolling: 'touch',
      }}>

        {/* Call-out boxes — the most important facts */}
        {EMERGENCY_DISCLAIMER.callouts.map((line, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '10px 12px',
            background: i === 0 ? 'rgba(220,38,38,0.12)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${i === 0 ? 'rgba(220,38,38,0.3)' : 'rgba(255,255,255,0.08)'}`,
            borderRadius: 9,
            marginBottom: 8,
          }}>
            <div style={{
              fontSize: 13, lineHeight: 1.4,
              color: i === 0 ? '#fca5a5' : '#d1d5db',
              fontWeight: i === 0 ? 800 : 600,
            }}>
              {line}
            </div>
          </div>
        ))}

        {/* Body text */}
        <div style={{
          fontSize: 13, color: '#9ca3af', lineHeight: 1.65,
          marginTop: 16, marginBottom: 16,
          whiteSpace: 'pre-line',
        }}>
          {EMERGENCY_DISCLAIMER.body}
        </div>

        {/* Full ToS link */}
        <button
          onClick={() => setShowFull((v) => !v)}
          style={{
            background: 'none', border: 'none', padding: 0,
            fontSize: 12, color: '#60a5fa', cursor: 'pointer',
            textDecoration: 'underline', fontFamily: T.font,
            marginBottom: showFull ? 12 : 20,
            display: 'block',
          }}
        >
          {showFull ? 'Hide full Terms of Service ↑' : 'Read full Terms of Service ↓'}
        </button>

        {/* Full ToS (collapsed by default) */}
        {showFull && <FullToS />}

        {/* Explicit checkbox */}
        <label style={{
          display: 'flex', gap: 12, alignItems: 'flex-start',
          padding: '14px',
          background: checked ? 'rgba(5,150,105,0.08)' : 'rgba(255,255,255,0.04)',
          border: `1.5px solid ${checked ? 'rgba(5,150,105,0.4)' : 'rgba(255,255,255,0.12)'}`,
          borderRadius: 10,
          cursor: 'pointer',
          marginBottom: 16,
          transition: 'all 0.15s',
        }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            style={{ width: 20, height: 20, cursor: 'pointer', flexShrink: 0, marginTop: 1 }}
          />
          <div style={{ fontSize: 13, color: '#d1d5db', lineHeight: 1.5 }}>
            {EMERGENCY_DISCLAIMER.affirmation}
          </div>
        </label>

        <div style={{ height: 8 }} />
      </div>

      {/* Bottom action */}
      <div style={{
        padding: '16px 20px 32px',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        background: '#0a0a0a',
        flexShrink: 0,
      }}>
        <button
          onClick={accept}
          disabled={!checked}
          style={{
            width: '100%', padding: '15px',
            borderRadius: 12, border: 'none',
            background: checked ? '#dc2626' : '#1f1f1f',
            color: checked ? '#fff' : '#4b5563',
            fontSize: 15, fontWeight: 900,
            cursor: checked ? 'pointer' : 'not-allowed',
            fontFamily: T.font,
            transition: 'all 0.15s',
            boxShadow: checked ? '0 4px 20px rgba(220,38,38,0.35)' : 'none',
          }}
        >
          {checked ? 'I Understand — Enter App' : 'Check the box to continue'}
        </button>

        {/* Hard 911 link — always visible */}
        <a
          href="tel:911"
          style={{
            display: 'block', textAlign: 'center',
            marginTop: 12, padding: '10px',
            fontSize: 13, fontWeight: 800, color: '#dc2626',
            textDecoration: 'none',
          }}
        >
          📞 Call 911 for emergencies
        </a>
      </div>
    </div>
  );
}

// ── Full Terms of Service (inline, collapsed) ─────────────────────
function FullToS() {
  const { TERMS_OF_SERVICE } = require('@data/legal');
  return (
    <div style={{
      background: 'rgba(0,0,0,0.4)',
      border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: 10,
      padding: '14px 14px',
      marginBottom: 20,
    }}>
      <div style={{
        fontSize: 10, fontWeight: 800, color: '#6b7280',
        textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12,
      }}>
        Terms of Service · Last updated {TERMS_OF_SERVICE.lastUpdated}
      </div>
      {TERMS_OF_SERVICE.sections.map((s) => (
        <div key={s.id} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#d1d5db', marginBottom: 4 }}>
            {s.title}
          </div>
          <div style={{
            fontSize: 10, color: '#6b7280', lineHeight: 1.7,
            whiteSpace: 'pre-line',
          }}>
            {s.body}
          </div>
        </div>
      ))}
    </div>
  );
}
